/* ==========================================================================
   Provider Landing Page
   Public-facing landing page for a pharmacy's provider portal.
   Shows catalog, pharmacy branding, and a request-access form.
   ========================================================================== */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom'
import { usePortal, usePortalSlug } from '@/context/PortalContext'
import { useAuth } from '@/hooks/useAuth'
import { getPublicCatalog, submitAccessRequest } from '@/api/portal'
import { ApiClientError } from '@/api/client'
import type { PublicCatalogProductDetail, AccessRequestCreate } from '@/api/types'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Select } from '@/components/ui/Select'
import { Alert } from '@/components/ui/Alert'
import { Textarea } from '@/components/ui/Textarea'
import { PharmacyLogo } from '@/components/ui/PharmacyLogo'
import { US_STATES } from '@/utils/usStates'
import { compareCategories } from '@/utils/sort'
import {
  DEFAULT_COUNTRY,
  isCanada,
  regionLabel,
  regionOptions,
  regionPlaceholder,
} from '@/utils/countries'

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

/**
 * Group catalog products by their full category list (joined), matching the
 * legacy single-string display — multi-category products show one combined
 * header. Products without categories fall under "Uncategorized".
 */
function groupByCategory(
  products: PublicCatalogProductDetail[]
): Record<string, PublicCatalogProductDetail[]> {
  const groups: Record<string, PublicCatalogProductDetail[]> = {}
  for (const product of products) {
    const category = product.categories.join(' / ') || 'Uncategorized'
    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(product)
  }
  return groups
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ProviderLanding() {
  const navigate = useNavigate()
  const location = useLocation()
  const { slug } = useParams<{ slug: string }>()
  const portalSlug = usePortalSlug()
  const { config, loading: portalLoading } = usePortal()
  const { isAuthenticated } = useAuth()

  // Catalog state
  const [products, setProducts] = useState<PublicCatalogProductDetail[]>([])
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  // Access request form state
  const [formName, setFormName] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formNpi, setFormNpi] = useState('')
  const [formLicenseState, setFormLicenseState] = useState<string | null>(null)
  const [formPhone, setFormPhone] = useState('')
  const [formMessage, setFormMessage] = useState('')
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [formSuccess, setFormSuccess] = useState(false)

  // Redirect authenticated users to the prescribe page
  useEffect(() => {
    if (isAuthenticated && slug) {
      navigate(`/rx/${slug}/prescribe`, { replace: true })
    }
  }, [isAuthenticated, slug, navigate])

  // Redirect to login if landing page is disabled
  useEffect(() => {
    if (!portalLoading && config && config.landing_page_enabled === false && slug) {
      navigate(`/rx/${slug}/login`, { replace: true })
    }
  }, [portalLoading, config, slug, navigate])

  // Fetch public catalog (only when the catalog section is enabled)
  useEffect(() => {
    if (!portalSlug) return
    if (config && config.show_catalog_on_landing_page === false) {
      setCatalogLoading(false)
      return
    }

    let cancelled = false

    async function fetchCatalog() {
      setCatalogLoading(true)
      setCatalogError(null)
      try {
        const response = await getPublicCatalog(portalSlug)
        if (!cancelled) {
          setProducts(response.products)
          setCatalogLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setCatalogError(
            err instanceof ApiClientError
              ? err.getUserMessage()
              : 'Failed to load catalog.'
          )
          setCatalogLoading(false)
        }
      }
    }

    fetchCatalog()

    return () => {
      cancelled = true
    }
  }, [portalSlug, config])

  // Group products by category
  const groupedProducts = useMemo(() => groupByCategory(products), [products])
  const categories = useMemo(
    () => Object.keys(groupedProducts).sort(compareCategories),
    [groupedProducts]
  )

  // Pharmacy name from config
  const pharmacyName = config?.pharmacy_name || 'Pharmacy'

  // Landing page section visibility (issue #107). Default to true so the
  // page renders normally while config is still loading or for older
  // pharmacy records that pre-date the toggles.
  const showCatalog = config?.show_catalog_on_landing_page ?? true
  const showInviteForm = config?.show_invite_form_on_landing_page ?? true

  // Canadian pharmacies (PORTAL-162): no NPI field, and the license field is a
  // Province rather than a State.
  const pharmacyCountry = config?.country ?? DEFAULT_COUNTRY
  const isCanadianPharmacy = isCanada(pharmacyCountry)
  const licenseRegionOptions = regionOptions(pharmacyCountry) ?? US_STATES

  // Form validation (simple inline)
  const formValid = formName.trim() !== '' && formEmail.trim() !== ''

  // Handle access request submission
  const handleAccessRequestSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!formValid) return

    setFormSubmitting(true)
    setFormError(null)

    try {
      const data: AccessRequestCreate = {
        name: formName.trim(),
        email: formEmail.trim(),
        npi: isCanadianPharmacy ? null : formNpi.trim() || null,
        license_state: formLicenseState || null,
        phone: formPhone.trim() || null,
        message: formMessage.trim() || null,
      }

      await submitAccessRequest(portalSlug, data)
      setFormSuccess(true)
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 429) {
          setFormError('Too many requests. Please wait a moment and try again.')
        } else {
          setFormError(err.getUserMessage())
        }
      } else {
        setFormError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setFormSubmitting(false)
    }
  }

  // Scroll to request-access section
  const scrollToRequestAccess = () => {
    const element = document.getElementById('request-access')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Jump to the form when arriving via the login/forgot-password "request
  // access" link (PORTAL-198). Waits for the catalog, which sits above the form.
  useEffect(() => {
    if (location.hash === '#request-access' && !portalLoading && !catalogLoading) {
      document.getElementById('request-access')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [location.hash, portalLoading, catalogLoading])

  // Don't render while checking redirects
  if (isAuthenticated) return null
  if (portalLoading) return null
  if (config && config.landing_page_enabled === false) return null

  return (
    <div className="min-h-screen bg-warm-beige">
      {/* ================================================================
          Header
          ================================================================ */}
      <header className="bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PharmacyLogo size="md" />
            <span className="text-lg font-semibold text-charcoal hidden sm:inline">
              {pharmacyName}
            </span>
          </div>
          <Link to={`/rx/${slug}/login`}>
            <Button variant="secondary" size="sm">
              Provider Login
            </Button>
          </Link>
        </div>
      </header>

      {/* ================================================================
          Hero Section
          ================================================================ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-charcoal mb-6">
            Welcome to {pharmacyName}&rsquo;s Provider Portal
          </h1>
          <p className="text-lg text-charcoal/70 mb-10 max-w-2xl mx-auto">
            Submit prescriptions quickly and securely through our digital portal.
            Browse our product catalog below and request access to get started.
          </p>
          {/* Buttons stacked, each with helper text beside it telling
              existing vs new providers which one to use (PORTAL-197). */}
          <div className="sm:w-fit mx-auto flex flex-col gap-6 sm:gap-4">
            <div className="flex flex-col sm:flex-row-reverse items-center gap-3 sm:gap-6">
              <p className="sm:w-80 md:w-[26rem] text-sm text-charcoal/70 sm:text-left">
                Already have an account? Click here to login
              </p>
              <Link to={`/rx/${slug}/login`} className="shrink-0">
                <Button variant="primary" size="lg" className="w-48">
                  Provider Login
                </Button>
              </Link>
            </div>
            {showInviteForm && (
              <div className="flex flex-col sm:flex-row-reverse items-center gap-3 sm:gap-6">
                <p className="sm:w-80 md:w-[26rem] text-sm text-charcoal/70 sm:text-left">
                  No account or pharmacy invite? Click here to request access
                </p>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={scrollToRequestAccess}
                  className="w-48 shrink-0"
                >
                  Request Access
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ================================================================
          Catalog Section
          ================================================================ */}
      {showCatalog && (
      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-10">
            Our Product Catalog
          </h2>

          {/* Loading state */}
          {catalogLoading && (
            <div className="text-center text-charcoal/60 py-12">
              Loading catalog...
            </div>
          )}

          {/* Error state */}
          {catalogError && (
            <Alert variant="error" className="max-w-xl mx-auto">
              {catalogError}
            </Alert>
          )}

          {/* Empty state */}
          {!catalogLoading && !catalogError && products.length === 0 && (
            <div className="text-center text-charcoal/60 py-12">
              No products are currently available.
            </div>
          )}

          {/* Products grouped by category */}
          {!catalogLoading && !catalogError && products.length > 0 && (
            <div className="space-y-10">
              {categories.map((category) => (
                <div key={category}>
                  <h3 className="font-serif text-xl text-charcoal mb-4 border-b border-gray-200 pb-2">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupedProducts[category].map((product, index) => (
                      <Card key={`${product.product_code}-${index}`}>
                        <h4 className="font-semibold text-charcoal mb-2 break-words">
                          {product.display_name}
                          {product.strength && (
                            <span className="ml-2 inline-block text-sm font-normal text-charcoal/60">
                              {product.strength}
                            </span>
                          )}
                        </h4>
                        <div className="space-y-1 text-sm text-charcoal/70">
                          <p>
                            <span className="font-medium text-charcoal/80">SIG:</span>{' '}
                            {product.dose_specific_directions || '—'}
                          </p>
                          <p>
                            <span className="font-medium text-charcoal/80">Qty:</span>{' '}
                            {product.quantity ? `${product.quantity} ${product.unit_amount}` : '—'}
                          </p>
                          <p>
                            <span className="font-medium text-charcoal/80">Days Supply:</span>{' '}
                            {product.days_supply ?? '—'}
                          </p>
                          <p>
                            <span className="font-medium text-charcoal/80">Refills:</span>{' '}
                            {product.refills ?? '—'}
                          </p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* ================================================================
          Request Access Form
          ================================================================ */}
      {showInviteForm && (
      <section id="request-access" className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-xl mx-auto">
          <Card>
            <h2 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-2">
              Request Access
            </h2>
            <p className="text-center text-charcoal/70 mb-6">
              Fill out the form below to request provider access to {pharmacyName}&rsquo;s portal.
            </p>

            {formSuccess ? (
              /* Success confirmation */
              <Alert variant="success" title="Request Submitted">
                Thank you! Your access request has been submitted. The pharmacy team will
                review your request and reach out to you shortly.
              </Alert>
            ) : (
              /* Access request form */
              <>
                {formError && (
                  <Alert variant="error" className="mb-6">
                    {formError}
                  </Alert>
                )}

                <form onSubmit={handleAccessRequestSubmit} noValidate>
                  <div className="space-y-5">
                    {/* Name */}
                    <FormField label="Name" required htmlFor="access-name">
                      <Input
                        id="access-name"
                        type="text"
                        autoComplete="name"
                        placeholder="Your full name"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        disabled={formSubmitting}
                      />
                    </FormField>

                    {/* Email */}
                    <FormField label="Email" required htmlFor="access-email">
                      <Input
                        id="access-email"
                        type="email"
                        autoComplete="email"
                        placeholder="your@email.com"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        disabled={formSubmitting}
                      />
                    </FormField>

                    {/* NPI (US pharmacies only) */}
                    {!isCanadianPharmacy && (
                    <FormField label="NPI" htmlFor="access-npi" helperText="10-digit NPI number (optional)">
                      <Input
                        id="access-npi"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="NPI number"
                        value={formNpi}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                          setFormNpi(digits)
                        }}
                        disabled={formSubmitting}
                        maxLength={10}
                      />
                    </FormField>
                    )}

                    {/* License State / Province */}
                    <FormField label={`License ${regionLabel(pharmacyCountry)}`} htmlFor="access-license-state">
                      <Select
                        options={licenseRegionOptions}
                        value={formLicenseState}
                        onChange={(value) => setFormLicenseState(value)}
                        placeholder={regionPlaceholder(pharmacyCountry)}
                        disabled={formSubmitting}
                      />
                    </FormField>

                    {/* Phone */}
                    <FormField label="Phone" htmlFor="access-phone">
                      <Input
                        id="access-phone"
                        type="tel"
                        autoComplete="tel"
                        placeholder="(555) 555-5555"
                        value={formPhone}
                        onChange={(e) => setFormPhone(e.target.value)}
                        disabled={formSubmitting}
                      />
                    </FormField>

                    {/* Message */}
                    <FormField label="Message" htmlFor="access-message">
                      <Textarea
                        id="access-message"
                        placeholder="Any additional information or questions..."
                        value={formMessage}
                        onChange={(e) => setFormMessage(e.target.value)}
                        disabled={formSubmitting}
                        rows={3}
                      />
                    </FormField>
                  </div>

                  {/* Submit button */}
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    loading={formSubmitting}
                    disabled={formSubmitting || !formValid}
                    className="w-full mt-8"
                  >
                    Submit Request
                  </Button>
                </form>
              </>
            )}
          </Card>
        </div>
      </section>
      )}

      {/* ================================================================
          Footer
          ================================================================ */}
      <footer className="py-8 px-4 sm:px-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto text-center text-sm text-charcoal/50">
          <div>Powered by TJM Labs</div>
          <div className="mt-2">
            <Link
              to={`/rx/${slug}/admin/login`}
              className="text-xs text-charcoal/40 hover:text-charcoal/60 transition-colors"
            >
              Pharmacy Staff Login
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default ProviderLanding
